import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import CustomFieldsModal from '../../components/CustomFieldsModal';

const SubCategories = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [category, setCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getCategoryData = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: `categories/${id}`,
      });
      if (response.data.status) {
        setCategory(response.data.data);
        // Ensure fields array is initialized for each subcategory
        const subCats = (response.data.sub_categories || []).map(subCat => ({
          ...subCat,
          fields: subCat.fields || []
        }));
        console.log('Subcategories loaded:', subCats);
        setSubCategories(subCats);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch category data');
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => {
    if (id) {
      getCategoryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubCategoryChange = (index, field, value) => {
    const updatedSubCategories = [...subCategories];
    updatedSubCategories[index][field] = value;
    setSubCategories(updatedSubCategories);
  };

  const handleImageChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const updatedSubCategories = [...subCategories];
      updatedSubCategories[index].image = file;
      updatedSubCategories[index].imagePreview = URL.createObjectURL(file);
      setSubCategories(updatedSubCategories);
    }
  };

  const addNewSubCategory = () => {
    setSubCategories([...subCategories, { 
      title: '', 
      title_ar: '', 
      image: null, 
      imagePreview: null,
      fields: []
    }]);
  };

  const openCustomFieldsModal = (subCat, index) => {
    setSelectedSubCategory({ ...subCat, index });
    setIsModalOpen(true);
  };

  const closeCustomFieldsModal = () => {
    setIsModalOpen(false);
    setSelectedSubCategory(null);
  };

  const handleFieldsSaved = async (subCategoryId, fields) => {
    // Update the subcategory in the list with the saved fields immediately
    const updatedSubCategories = subCategories.map(subCat => {
      if (subCat._id === subCategoryId) {
        return { ...subCat, fields };
      }
      return subCat;
    });
    setSubCategories(updatedSubCategories);
    // Refresh the data to get the latest from server
    await getCategoryData();
  };

  const removeSubCategory = (index) => {
    const updatedSubCategories = subCategories.filter((_, i) => i !== index);
    setSubCategories(updatedSubCategories);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoader(true);

    try {
      const formData = new FormData();
      
      // Append each subcategory data (fields are managed separately via modal)
      subCategories.forEach((subCat, index) => {
        formData.append(`subCategories[${index}][_id]`, subCat._id || '');
        formData.append(`subCategories[${index}][title]`, subCat.title);
        formData.append(`subCategories[${index}][title_ar]`, subCat.title_ar);
        if (subCat.image instanceof File) {
          formData.append(`subCategories[${index}][image]`, subCat.image);
        }
        // If editing existing subcategory with existing image
        if (subCat.id && !subCat.image) {
          formData.append(`subCategories[${index}][id]`, subCat.id);
        }
      });

      const response = await ApiService.request({
        method: 'POST',
        url: `categories/${id}/subcategories`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status) {
        toast.success(response.data.message);
        navigate('/categories');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      toast.error('Failed to save subcategories');
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {category?.title || 'Category'} - Subcategories
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <Link to="/categories" className="text-muted text-hover-primary">Categories</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-body pt-0">
              <form onSubmit={handleSubmit} className="form fv-plugins-bootstrap5 fv-plugins-framework">
                <div className="mb-10">
                  <h3 className="my-5">Category: {category?.title}</h3>
                  
                  <div className="d-flex justify-content-between align-items-center mb-5">
                    <h4>Subcategories</h4>
                    <button 
                      type="button" 
                      className="btn btn-base btn-sm"
                      onClick={addNewSubCategory}
                    >
                      Add Subcategory
                    </button>
                  </div>

                  {subCategories.map((subCat, index) => (
                    <div key={index} className="card bg-light mb-5">
                      <div className="card-body">
                        <div className="row g-9">
                          <div className="col-md-4 fv-row fv-plugins-icon-container">
                            <label className="required fs-6 fw-semibold mb-2">Title</label>
                            <input
                              type="hidden"
                              className="form-control"
                              required
                              value={subCat._id || ''}
                              onChange={(e) => handleSubCategoryChange(index, '_id', e.target.value)}
                            />
                            <input
                              type="text"
                              className="form-control "
                              placeholder="Enter Subcategory Title"
                              required
                              value={subCat.title || ''}
                              onChange={(e) => handleSubCategoryChange(index, 'title', e.target.value)}
                            />
                          </div>
                          <div className="col-md-4 fv-row fv-plugins-icon-container">
                            <label className="required fs-6 fw-semibold mb-2">Title Arabic</label>
                            <input
                              type="text"
                              className="form-control "
                              placeholder="Enter Subcategory Title Arabic"
                              required
                              value={subCat.title_ar || ''}
                              onChange={(e) => handleSubCategoryChange(index, 'title_ar', e.target.value)}
                            />
                          </div>
                          <div className="col-md-3 fv-row fv-plugins-icon-container">
                            <label className="fs-6 fw-semibold mb-2">Image</label>
                            <div className="d-flex align-items-center">
                              <input
                                type="file"
                                className="form-control "
                                accept="image/*"
                                onChange={(e) => handleImageChange(index, e)}
                              />
                              {subCat.imagePreview || subCat.image ? (
                                <img 
                                  src={subCat.imagePreview || subCat.image} 
                                  alt="Preview" 
                                  className="ms-2 rounded"
                                  style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-1 fv-row fv-plugins-icon-container d-flex align-items-end">
                            <button
                              type="button"
                              className="btn btn-icon btn-sm btn-danger"
                              onClick={() => removeSubCategory(index)}
                            >
                              <i className="bi bi-x fs-5"></i>
                            </button>
                          </div>
                        </div>
                        {subCat._id && (
                          <div className="row g-9 mt-3">
                            <div className="col-12">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  {(() => {
                                    const fieldsArray = Array.isArray(subCat.fields) ? subCat.fields : [];
                                    const hasFields = fieldsArray.length > 0;
                                    return (
                                      <>
                                        <span className="badge badge-light-primary me-2">
                                          {hasFields
                                            ? `${fieldsArray.length} field${fieldsArray.length > 1 ? 's' : ''}`
                                            : 'No fields'
                                          }
                                        </span>
                                        {hasFields && (
                                          <small className="text-muted ms-2">
                                            {fieldsArray.slice(0, 2).map((field, idx) => (
                                              <span key={idx}>
                                                {field?.title || 'Untitled'}
                                                {idx < Math.min(fieldsArray.length, 2) - 1 ? ', ' : ''}
                                              </span>
                                            ))}
                                            {fieldsArray.length > 2 && '...'}
                                          </small>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-base"
                                  onClick={() => openCustomFieldsModal(subCat, index)}
                                >
                                  <i className="bi bi-gear text-white me-1"></i>
                                  Custom Fields
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="row g-9 my-3">
                  <div className="text-center">
                    <Link to="/categories" className="btn btn-light me-3">Back</Link>
                    <button type="submit" className="btn btn-base" disabled={isLoader}>
                      {isLoader ? <Loader size="20" color="white" text="Saving" /> : 'Save'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Fields Modal */}
      {selectedSubCategory && (
        <CustomFieldsModal
          isOpen={isModalOpen}
          onClose={closeCustomFieldsModal}
          subCategoryId={selectedSubCategory._id}
          subCategoryTitle={selectedSubCategory.title}
          initialFields={selectedSubCategory.fields || []}
          onSave={handleFieldsSaved}
        />
      )}
    </div>
  );
};

export default SubCategories;